import React, { useEffect, useState } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import { PartnerListingPageContainer } from "../PartnerListingPage/PartnerListingPageContainer";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import logo from "../../assets/images/imagePlaceholder.png";
import { useNavigate } from "react-router-dom";
import { smaartCardUrl } from "../../constant";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const MapMarker = () => {
  const [currentPosition, setCurrentPosition] = useState(null);
  const [mapsApi, setMapsApi] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch nearby partners from API
  const fetchNearbyPartners = async (lat, lng) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${smaartCardUrl}/api/v1/partner/nearby?lat=${lat}&lng=${lng}`,
        {
          method: "GET",
          headers: {
            "X-APIKEY": "dfordr",
            "AgentName": "dfordrapp",
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status && result.response) {
        // Transform API response to match your component structure
        const transformedPartners = result.response.map(partner => ({
          id: partner.partnerId,
          partnerId: partner.partnerId,
          partnerName: partner.name,
          lat: partner.latitude,
          lng: partner.longitude,
          category: partner.categoryMaster?.typeName || "General",
          specialization: partner.specialization || "Not Specified",
          email: partner.email,
          phone: partner.mobile,
          profilePhoto: partner.profilePhoto,
          address: partner.address,
          city: partner.city,
          state: partner.state,
          isActive: partner.isActive,
          categoryMaster: partner.categoryMaster
        }));
        
        setPartners(transformedPartners);
        setError(null);
      } else {
        throw new Error(result.message || "Failed to fetch partners");
      }
    } catch (err) {
      console.error("Error fetching partners:", err);
      setError(err.message || "Failed to load nearby partners");
      // Fallback to static data if API fails
      setPartners(getFallbackPartners());
    } finally {
      setLoading(false);
    }
  };

  // Fallback data in case API fails
  const getFallbackPartners = () => [
    {
      id: 1,
      partnerId: 1,
      lat: 23.0225,
      lng: 72.5714,
      partnerName: "Ahmedabad Center",
      category: "Eyewear",
      specialization: "General Medicine",
      email: "ahmcenter@vasyerp.com",
      phone: "8128555555",
      profilePhoto: null,
      address: "Default Address",
      isActive: 1
    },
    {
      id: 2,
      partnerId: 2,
      lat: 23.0258,
      lng: 72.5700,
      partnerName: "Law Garden Cafe",
      category: "Bakery",
      specialization: "Cafe Items",
      email: "lawgarden@vasyerp.com",
      phone: "8128555555",
      profilePhoto: null,
      address: "Default Address",
      isActive: 1
    },
  ];

  useEffect(() => {
    // Get current location first, then fetch partners
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentPosition(userPosition);
        
        // Fetch partners based on user's current location
        fetchNearbyPartners(userPosition.lat, userPosition.lng);
      },
      (error) => {
        console.error("Error getting location:", error);
        const defaultPosition = { lat: 23.0225, lng: 72.5714 }; // Default to Ahmedabad
        setCurrentPosition(defaultPosition);
        fetchNearbyPartners(defaultPosition.lat, defaultPosition.lng);
      }
    );
  }, []);

  const onMapLoad = () => {
    setMapsApi(window.google.maps);
  };

  const handlePartnerClick = (partner) => {
    // Navigate to partner details page with partner data
    navigate(`/partner/${partner.partnerName.toLowerCase().replace(/\s+/g, '-')}/${partner.partnerId}`, {
      state: { partner }
    });
  };

  // Function to get appropriate marker color based on partner status
  const getMarkerIcon = (partner) => {
    if (!mapsApi) return "";
    
    const baseUrl = "https://maps.google.com/mapfiles/ms/icons/";
    
    if (partner.isActive === 0) {
      return baseUrl + "green-dot.png"; // Inactive partners
    }
    
    return baseUrl + "blue-dot.png"; // Active partners
  };

  return (
    <PartnerListingPageContainer>
      <Header />
      
      {/* Loading State */}
      {loading && (
        <div style={{ 
          textAlign: "center", 
          padding: "20px",
          background: "#f0f7ff",
          margin: "10px",
          borderRadius: "8px"
        }}>
          Loading nearby partners...
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div style={{ 
          textAlign: "center", 
          padding: "10px",
          background: "#ffe0e0",
          color: "#d00",
          margin: "10px",
          borderRadius: "4px",
          fontSize: "14px"
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Partners Count */}
      {!loading && partners.length > 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: "10px",
          background: "#e8f5e8",
          margin: "10px",
          borderRadius: "4px",
          fontSize: "14px",
          fontWeight: "bold"
        }}>
          📍 Found {partners.length} partners near you
        </div>
      )}

      <LoadScript googleMapsApiKey="AIzaSyDZki79RP5qfiGJk2SqlJdhZkQbLBFMnC8">
        {currentPosition && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={currentPosition}
            zoom={12}
            onLoad={onMapLoad}
          >
            {/* Current Position Marker */}
            {mapsApi && (
              <Marker
                position={currentPosition}
                icon={{
                  url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                  scaledSize: new mapsApi.Size(30, 30),
                }}
                title="Your Location"
              />
            )}

            {/* Partner Markers and Cards */}
            {mapsApi && partners.map((partner) => (
              <React.Fragment key={partner.partnerId}>
                <Marker
                  position={{ lat: partner.lat, lng: partner.lng }}
                  icon={{
                    url: getMarkerIcon(partner),
                    scaledSize: new window.google.maps.Size(28, 28),
                  }}
                  title={partner.partnerName}
                />
                <OverlayView
                  position={{ lat: partner.lat, lng: partner.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    onClick={() => handlePartnerClick(partner)}
                    style={{
                      background: partner.isActive === 0 ? "#f5f5f5" : "#f0f7ff",
                      borderRadius: "8px",
                      padding: "12px",
                      width: "230px",
                      display: "flex",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      transform: "translate(-50%, 10px)",
                      cursor: "pointer",
                      border: partner.isActive === 0 ? "1px dashed #ccc" : "1px solid #83bfa1",
                      opacity: partner.isActive === 0 ? 0.7 : 1
                    }}
                  >
                    <div style={{ marginRight: "12px" }}>
                      <img
                        src={partner.profilePhoto || logo}
                        alt={partner.partnerName}
                        style={{ 
                          borderRadius: "8px", 
                          width: "40px", 
                          height: "40px",
                          objectFit: "cover" 
                        }}
                        onError={(e) => {
                          e.target.src = logo;
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: "12px", 
                        fontWeight: "bold", 
                        color: partner.isActive === 0 ? "#888" : "#111" 
                      }}>
                        {partner.partnerName}
                        {partner.isActive === 0 && (
                          <span style={{
                            fontSize: "8px",
                            color: "#ff6b6b",
                            marginLeft: "5px",
                            background: "#ffeaea",
                            padding: "1px 4px",
                            borderRadius: "3px"
                          }}>
                            Inactive
                          </span>
                        )}
                      </h3>
                      <div style={{ 
                        fontSize: "10px", 
                        color: partner.isActive === 0 ? "#999" : "#444",
                        marginTop: "2px"
                      }}>
                        {partner.category}
                      </div>
                      <div style={{ 
                        fontSize: "9px", 
                        marginTop: "4px",
                        color: partner.isActive === 0 ? "#999" : "#333"
                      }}>
                        <strong>Specialization:</strong>{" "}
                        <span style={{
                          backgroundColor: partner.isActive === 0 ? "#ccc" : "#83bfa1",
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "8px",
                        }}>
                          {partner.specialization}
                        </span>
                      </div>
                      <div style={{ 
                        fontSize: "9px", 
                        marginTop: "4px", 
                        color: partner.isActive === 0 ? "#999" : "#222" 
                      }}>
                        📞 {partner.phone}
                      </div>
                      <div style={{ 
                        fontSize: "8px", 
                        marginTop: "4px", 
                        color: partner.isActive === 0 ? "#999" : "#666",
                        fontStyle: "italic"
                      }}>
                        {partner.city}, {partner.state}
                      </div>
                    </div>
                  </div>
                </OverlayView>
              </React.Fragment>
            ))}
          </GoogleMap>
        )}
      </LoadScript>
      <Footer />
    </PartnerListingPageContainer>
  );
};

export default MapMarker;